import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
} from '@mui/material';
import {
  DragIndicator,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Delete,
} from '@mui/icons-material';
import { RootState } from '../../store/store';
import {
  reorderWaypoints,
  removeWaypoint,
  setHoveredWaypoint,
} from '../../store/slices/missionPlannerSlice';
import { PlannerWaypoint } from '../../types/missionPlanner';
import { formatLatLng } from '../../utils/formatters';

// WCAG 2.5.5 / touch: interactive rows and controls are at least 44px.
const TOUCH = 44;

interface ItemProps {
  waypoint: PlannerWaypoint;
  index: number;
  count: number;
}

/** One sortable, keyboard-reorderable waypoint row with a context menu. */
const SortableWaypointItem: React.FC<ItemProps> = ({
  waypoint,
  index,
  count,
}) => {
  const dispatch = useDispatch();
  const hoveredId = useSelector(
    (state: RootState) => state.missionPlanner.hoveredWaypointId
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: waypoint.id });

  const move = (to: number) => {
    dispatch(reorderWaypoints({ from: index, to }));
  };

  // Alt+Up / Alt+Down reorder without entering drag mode (AC US-4).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!e.altKey) return;
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      move(index - 1);
    } else if (e.key === 'ArrowDown' && index < count - 1) {
      e.preventDefault();
      move(index + 1);
    }
  };

  const closeMenu = () => setMenuAnchor(null);
  const isHovered = hoveredId === waypoint.id;

  return (
    <ListItem
      ref={setNodeRef}
      onKeyDown={onKeyDown}
      onMouseEnter={() => dispatch(setHoveredWaypoint(waypoint.id))}
      onMouseLeave={() => dispatch(setHoveredWaypoint(null))}
      onFocus={() => dispatch(setHoveredWaypoint(waypoint.id))}
      onBlur={() => dispatch(setHoveredWaypoint(null))}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      sx={{
        minHeight: TOUCH,
        bgcolor: isHovered ? 'action.hover' : 'transparent',
        borderRadius: 1,
      }}
      secondaryAction={
        <IconButton
          edge="end"
          aria-label={`Waypoint ${waypoint.order} actions`}
          aria-haspopup="menu"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ width: TOUCH, height: TOUCH }}
        >
          <MoreVert />
        </IconButton>
      }
    >
      {/* Drag handle — the only drag-activating surface, so the row stays
          clickable/scrollable. 44px touch target. */}
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Reorder waypoint ${waypoint.order}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: TOUCH,
          height: TOUCH,
          mr: 1,
          cursor: 'grab',
          color: 'text.secondary',
          touchAction: 'none',
        }}
      >
        <DragIndicator />
      </Box>

      <ListItemText
        primary={`Waypoint ${waypoint.order}`}
        secondary={formatLatLng(waypoint.lat, waypoint.lng)}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          disabled={index === 0}
          onClick={() => {
            move(index - 1);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ArrowUpward fontSize="small" />
          </ListItemIcon>
          Move up
        </MenuItem>
        <MenuItem
          disabled={index === count - 1}
          onClick={() => {
            move(index + 1);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ArrowDownward fontSize="small" />
          </ListItemIcon>
          Move down
        </MenuItem>
        <MenuItem
          onClick={() => {
            dispatch(removeWaypoint(waypoint.id));
            closeMenu();
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </ListItem>
  );
};

/**
 * Drag-and-drop reorderable waypoint list (issue #295). @dnd-kit gives us
 * built-in keyboard dragging (focus handle → Space → arrows → Space), and we
 * add Alt+Up/Down as a discoverable shortcut plus a per-row context menu.
 */
export const WaypointList: React.FC = () => {
  const dispatch = useDispatch();
  const waypoints = useSelector(
    (state: RootState) => state.missionPlanner.draft.waypoints
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = waypoints.findIndex((w) => w.id === active.id);
    const to = waypoints.findIndex((w) => w.id === over.id);
    dispatch(reorderWaypoints({ from, to }));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={waypoints.map((w) => w.id)}
        strategy={verticalListSortingStrategy}
      >
        <List dense aria-label="Waypoints">
          {waypoints.map((wp, i) => (
            <SortableWaypointItem
              key={wp.id}
              waypoint={wp}
              index={i}
              count={waypoints.length}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
};
